import type { Session, Tenant, User } from '@lensora/shared'
import * as React from 'react'


import { ONBOARDING_STORAGE_KEY } from '@/components/onboarding/persistence'
import { ACTIVE_WEBSITE_STORAGE_KEY } from '@/hooks/use-active-website'
import { ApiError, api } from '@/lib/api'
import { authClient } from '@/lib/auth'
import { PENDING_PLAN_KEY } from '@/lib/billing'
import { queryClient } from '@/lib/query-client'

type MeResponse = { user: User; tenant: Pick<Tenant, 'id' | 'slug' | 'name' | 'featureFlags'> | null }

// Query cache entries (e.g. ['me'], the ['lensora-admin', ...] family) are not
// scoped by user, so they must be dropped at every session boundary — both
// sign-in and sign-out — or the next person to authenticate in this tab can
// briefly see the previous user's cached profile/role and, for admin routes,
// cross-tenant data. localStorage keys below carry the same risk and aren't
// touched by queryClient.clear().
const SESSION_LOCAL_STORAGE_KEYS = [ACTIVE_WEBSITE_STORAGE_KEY, ONBOARDING_STORAGE_KEY, PENDING_PLAN_KEY]

type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; session: Session; user: User }
  | { status: 'unauthenticated' }

type AuthContextValue = AuthState & {
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = React.createContext<AuthContextValue | null>(null)

async function fetchMe(): Promise<User> {
  const data = await api.get<MeResponse>('/api/auth/me')
  return data.user
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<AuthState>({ status: 'loading' })
  // Blocks onAuthStateChange from updating state while signIn is mid-flight.
  // Without this, Supabase fires the event the moment credentials are accepted,
  // causing the dashboard to mount briefly before tenant verification completes.
  const isVerifying = React.useRef(false)

  React.useEffect(() => {
    // On mount, restore session and enrich with server-side profile (role, tenantId, etc.)
    authClient.getSession().then(async (session) => {
      if (!session?.user) {
        setState({ status: 'unauthenticated' })
        return
      }
      try {
        const me = await fetchMe()
        setState({ status: 'authenticated', session, user: me })
      } catch {
        // Don't sign out during a password recovery flow — wiping the recovery session
        // would prevent the user from being able to set their password.
        if (window.location.pathname.startsWith('/reset-password')) {
          setState({ status: 'unauthenticated' })
          return
        }
        // OAuth signups have a session but no profile yet — the completion
        // page owns this session and creates the profile.
        if (window.location.pathname.startsWith('/signup/complete')) {
          setState({ status: 'unauthenticated' })
          return
        }
        // Session exists but tenant check failed (disabled, wrong tenant, etc.) — sign out cleanly
        await authClient.signOut()
        setState({ status: 'unauthenticated' })
      }
    })

    // Keep in sync for token refresh and sign-out in other tabs.
    // Sign-in is handled by signIn() below; isVerifying guards against double-update.
    const unsubscribe = authClient.onAuthStateChange((session, event) => {
      if (isVerifying.current) return
      // Let the reset-password page own this session — don't interfere.
      if (event === 'PASSWORD_RECOVERY') return
      if (!session?.user) {
        setState({ status: 'unauthenticated' })
      }
      // Note: we don't re-fetch /me on token refresh — the user object from sign-in
      // is already enriched and role/tenantId don't change mid-session.
    })

    return unsubscribe
  }, [])

  async function signIn(email: string, password: string) {
    isVerifying.current = true
    // Drop any cache left by a previous session in this tab before the new
    // one starts, in case that session ended other than via signOut() below
    // (expiry, cross-tab sign-out) and so never got a chance to clear it.
    // localStorage must be dropped here too, not just the query cache: a
    // self-serve signup calls signIn() directly for the brand-new account
    // without ever calling signOut() on whatever session was active in this
    // tab beforehand, so the previous account's active-website id would
    // otherwise silently carry over — the new (or next logged-in) account's
    // dashboard, reports, etc. would resolve against a website it doesn't
    // even own.
    queryClient.clear()
    for (const key of SESSION_LOCAL_STORAGE_KEYS) {
      localStorage.removeItem(key)
    }
    try {
      const session = await authClient.signInWithPassword(email, password)

      let me: User
      try {
        me = await fetchMe()
      } catch (err) {
        await authClient.signOut()
        if (err instanceof ApiError && err.status === 403) {
          throw new Error(err.message ?? "Your account doesn't have access to this workspace.")
        }
        if (err instanceof ApiError && err.status === 404) {
          throw new Error("This workspace doesn't exist.")
        }
        throw err
      }

      setState({ status: 'authenticated', session, user: me })
    } finally {
      isVerifying.current = false
    }
  }

  async function signOut() {
    // finally, not just after await: if the remote signOut() call fails, the
    // local session must still be torn down rather than leaving the cache
    // and localStorage intact for whoever uses this tab next.
    try {
      await authClient.signOut()
    } finally {
      queryClient.clear()
      for (const key of SESSION_LOCAL_STORAGE_KEYS) {
        localStorage.removeItem(key)
      }
      setState({ status: 'unauthenticated' })
      // Full reload, not just the SPA redirect ProtectedLayout does on
      // `unauthenticated` state: it guarantees any in-memory state this audit
      // didn't catch (module-level singletons, component state) is gone too.
      // Scoped to this user-initiated path only — cross-tab sign-out is
      // handled by the onAuthStateChange listener below, which intentionally
      // does not reload other open tabs.
      window.location.href = '/login'
    }
  }

  return (
    <AuthContext.Provider value={{ ...state, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
